export const OPEN_CREATIVE_STUDIO_GIG_CAMPAIGN_EVENT = 'ss:open-creative-studio-gig-campaign';

const SOCIAL_LINK_FIELDS = [
  ['Instagram', 'instagramUrl'],
  ['Facebook', 'facebookUrl'],
  ['X', 'twitterUrl'],
  ['Pinterest', 'pinterestUrl'],
  ['TikTok', 'tiktokUrl'],
  ['YouTube', 'youtubeUrl'],
  ['Spotify', 'spotifyUrl'],
  ['SoundCloud', 'soundcloudUrl'],
  ['Mixcloud', 'mixcloudUrl'],
  ['Apple Music', 'appleMusicUrl'],
];

function isHistoricalPerformer(performer) {
  return ['cancelled', 'replaced'].includes(String(performer?.status || '').toLowerCase());
}

function formatDateRange(gig) {
  const start = gig?.startsAtLocal ? new Date(gig.startsAtLocal) : gig?.startsAtUtc ? new Date(gig.startsAtUtc) : null;
  const end = gig?.endsAtLocal ? new Date(gig.endsAtLocal) : gig?.endsAtUtc ? new Date(gig.endsAtUtc) : null;
  if (!start || Number.isNaN(start.getTime()) || !end || Number.isNaN(end.getTime())) {
    return '';
  }

  return `${start.toLocaleString()} to ${end.toLocaleString()}`;
}

function buildPerformerPromptLine(performer, index) {
  const name = String(performer?.displayName || '').trim() || `Performer ${index + 1}`;
  const parts = [`Name: ${name}`];
  const setStart = String(performer?.setStartTime || '').slice(0, 5);
  const setEnd = String(performer?.setEndTime || '').slice(0, 5);

  if (setStart || setEnd) {
    parts.push(`Set time: ${setStart || 'TBC'}-${setEnd || 'TBC'}`);
  }

  if (String(performer?.genresText || '').trim()) {
    parts.push(`Genres: ${String(performer.genresText).trim()}`);
  }

  if (String(performer?.bio || '').trim()) {
    parts.push(`Bio: ${String(performer.bio).trim()}`);
  }

  const links = SOCIAL_LINK_FIELDS
    .map(([label, key]) => {
      const value = String(performer?.[key] || '').trim();
      return value ? `${label}: ${value}` : '';
    })
    .filter(Boolean);

  if (links.length) {
    parts.push(`Links: ${links.join(' | ')}`);
  }

  parts.push(
    Number(performer?.artworkCreativeId || 0) > 0 || performer?.artworkThumbnailUrl
      ? 'Performer artwork: available'
      : 'Performer artwork: not provided'
  );

  return `- ${parts.join('. ')}.`;
}

export function buildGigCampaignRequest({ gig, siteName = '' } = {}) {
  const activePerformers = (Array.isArray(gig?.performers) ? gig.performers : [])
    .filter((performer) => performer && !isHistoricalPerformer(performer));
  const performerNames = activePerformers
    .map((performer) => String(performer?.displayName || '').trim())
    .filter(Boolean);
  const gigHasArtwork = Boolean(gig?.gigArtworkThumbnailUrl || Number(gig?.gigArtworkCreativeId || 0) > 0);
  const title = String(gig?.title || '').trim() || 'Untitled gig';
  const dateRange = formatDateRange(gig);
  const description = String(gig?.description || '').trim();
  const promptLines = [
    'Create a commercially strong promotional campaign for this published gig.',
    `Venue: ${String(siteName || '').trim() || 'Current venue'}.`,
    `Gig title: ${title}.`,
    dateRange ? `Gig timing: ${dateRange}.` : '',
    description ? `Gig description: ${description}.` : 'Gig description: not provided.',
    gigHasArtwork ? 'Dedicated gig artwork is already attached.' : 'No dedicated gig artwork is attached yet.',
    activePerformers.length
      ? `Featured performers: ${performerNames.join(', ')}.`
      : 'Featured performers: not provided. Keep the copy event-led and venue-safe.',
    'Use the event details and performer information to make the campaign feel specific and real.',
    'Do not invent ticket prices, sponsors, celebrity claims, or venue features that are not in the brief.',
  ].filter(Boolean);

  if (activePerformers.length) {
    promptLines.push('Performer details:');
    activePerformers.forEach((performer, index) => {
      promptLines.push(buildPerformerPromptLine(performer, index));
    });
  }

  const sourceSummary = performerNames.length
    ? `${title} featuring ${performerNames.join(', ')}`
    : title;

  return {
    source: 'scheduler_gig',
    sourceLabel: 'Scheduler / Gigs',
    gigId: Number(gig?.id || 0) || null,
    siteId: Number(gig?.siteId || 0) || null,
    gigTitle: title,
    performerNames,
    autoGenerateArtwork: !gigHasArtwork,
    prompt: promptLines.join('\n'),
    contextTitle: `Campaign Creator: ${title}`,
    contextBody: gigHasArtwork
      ? 'This published gig already has dedicated artwork, so Campaign Creator will generate copy and leave artwork selection under your control.'
      : 'This published gig does not have dedicated artwork yet, so Campaign Creator will generate both copy and artwork automatically.',
    sourceSummary,
  };
}

export function openGigCampaignInCreativeStudio(request) {
  if (typeof window === 'undefined' || !request) return;
  window.dispatchEvent(
    new CustomEvent(OPEN_CREATIVE_STUDIO_GIG_CAMPAIGN_EVENT, {
      detail: request,
    })
  );
}
